import { useEffect, useRef, useState } from "react";
import { AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as ww from "@wecom/jssdk";
import { CustomerMemoryIntake } from "@/components/journey/CustomerMemoryIntake";
import {
  WecomSidebarApiError,
  clearSidebarToken,
  createSidebarSession,
  fetchBootstrap,
  fetchCurrentContact,
  fetchJsapiConfig,
  oauthAuthorizeUrl,
  proposeSidebarMemory,
  readSidebarToken,
  stripHash,
  updateSidebarMemory,
  writeSidebarToken,
  type SidebarContact,
  type SidebarJourney,
} from "@/lib/wecom-sidebar";

type MatchedView = {
  kind: "matched";
  contact: SidebarContact;
  journey?: SidebarJourney;
  externalUserId: string;
};

type ViewState =
  | { kind: "loading"; hint: string }
  | MatchedView
  | { kind: "unsynced" }
  | { kind: "wrong_entry" }
  | { kind: "domain"; message: string }
  | { kind: "error"; message: string };

type IdentifyResult =
  | { ok: true; externalUserId: string }
  | { ok: false; reason: "wrong_entry" | "sdk"; message: string };

const SHANGHAI_TZ = "Asia/Shanghai";

export default function WecomSidebarPage() {
  const [view, setView] = useState<ViewState>({ kind: "loading", hint: "正在打开企微工具栏…" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void runSidebar();

    async function runSidebar() {
      try {
        const bootstrap = await fetchBootstrap();
        if (bootstrap.domain_status !== "active" && bootstrap.domain_status !== "issuing_cert") {
          setView({ kind: "domain", message: "域名证书尚未就绪，请稍后再试" });
          return;
        }
        if (!bootstrap.corp_id || !bootstrap.oauth_redirect_url) {
          setView({ kind: "error", message: "该账户尚未完成企微接入" });
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        let token = readSidebarToken();

        if (!token && !code) {
          window.location.replace(oauthAuthorizeUrl(bootstrap.corp_id, bootstrap.oauth_redirect_url));
          return;
        }

        if (code) {
          const session = await createSidebarSession(code);
          writeSidebarToken(session.access_token);
          token = session.access_token;
          params.delete("code");
          params.delete("state");
          const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
          window.history.replaceState({}, "", next);
        }

        if (!token) {
          setView({ kind: "wrong_entry" });
          return;
        }

        setView({ kind: "loading", hint: "正在识别当前客户…" });
        const identified = await resolveExternalContact(token, {
          corpId: bootstrap.corp_id,
          agentId: bootstrap.agent_id,
        });
        if (!identified.ok) {
          setView(
            identified.reason === "wrong_entry"
              ? { kind: "wrong_entry" }
              : { kind: "error", message: identified.message },
          );
          return;
        }

        const result = await fetchCurrentContact(token, identified.externalUserId);
        if (!result.found || !result.contact) {
          setView({ kind: "unsynced" });
          return;
        }
        setView({
          kind: "matched",
          contact: result.contact,
          journey: result.journey,
          externalUserId: identified.externalUserId,
        });
      } catch (error) {
        if (error instanceof WecomSidebarApiError && error.status === 401) {
          clearSidebarToken();
          setView({ kind: "error", message: error.message });
          return;
        }
        const message = error instanceof Error ? error.message : "打开工具栏失败";
        setView({ kind: "error", message });
      }
    }
  }, []);

  return (
    <main className="min-h-dvh bg-zinc-100 px-3 py-3 text-sm text-zinc-900">
      <div className="mx-auto flex max-w-md flex-col gap-2">
        <SidebarBody view={view} />
      </div>
    </main>
  );
}

function SidebarBody({ view }: { view: ViewState }) {
  if (view.kind === "loading") {
    return <StatusCard icon="loading" title="请稍候" body={view.hint} />;
  }

  if (view.kind === "matched") {
    return (
      <>
        <MatchedSection contact={view.contact} />
        <JourneySection journey={view.journey} />
        <MemorySection externalUserId={view.externalUserId} initial={view.contact.memory_summary} />
      </>
    );
  }

  const copy =
    view.kind === "unsynced"
      ? { title: "尚未同步", body: "当前聊天客户尚未同步到本账户" }
      : view.kind === "wrong_entry"
        ? { title: "入口不对", body: "请从外部客户的单聊工具栏打开" }
        : view.kind === "domain"
          ? { title: "域名未就绪", body: view.message }
          : { title: "无法识别", body: view.message };

  return <StatusCard title={copy.title} body={copy.body} />;
}

function StatusCard({
  title,
  body,
  icon = "alert",
}: {
  title: string;
  body: string;
  icon?: "loading" | "alert";
}) {
  return (
    <section className="flex items-start gap-2.5 rounded-[10px] bg-white px-3 py-2.5">
      {icon === "loading" ? (
        <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-zinc-400" aria-hidden />
      ) : (
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
      )}
      <div>
        <p className="text-[13px] font-semibold">{title}</p>
        <p className="mt-0.5 text-xs text-zinc-500">{body}</p>
      </div>
    </section>
  );
}

function MatchedSection({ contact }: { contact: SidebarContact }) {
  const follow = contact.follow_user_names.filter(Boolean).join("、") || "未记录跟进人";

  return (
    <section className="rounded-[10px] bg-white px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        {contact.avatar_url ? (
          <img src={contact.avatar_url} alt="" className="size-10 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-sm font-medium text-zinc-600">
            {(contact.name || "?").slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-[13px] font-semibold">{contact.name || "未命名客户"}</p>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              已对上
            </span>
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">跟进人 · {follow}</p>
        </div>
      </div>
      {contact.workspace_url ? (
        <a
          href={contact.workspace_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-600 underline-offset-2 hover:underline"
        >
          在工作台打开
          <ExternalLink className="size-3" aria-hidden />
        </a>
      ) : null}
    </section>
  );
}

function Section({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[10px] bg-white px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
        {hint ? <p className="text-[11px] text-zinc-400">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Meta({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] text-zinc-500">{label}</p>
      <p className="mt-0.5 text-[13px] font-medium leading-snug break-words text-zinc-800">{value}</p>
    </div>
  );
}

function JourneySection({ journey }: { journey?: SidebarJourney }) {
  if (!journey) return null;

  if (journey.error === "unavailable") {
    return (
      <Section label="旅程">
        <p className="mt-1 text-xs text-zinc-500">旅程信息暂时不可用</p>
      </Section>
    );
  }

  if (!journey.bound) {
    return (
      <Section label="旅程">
        <p className="mt-1 text-xs text-zinc-500">这位客户还没进入旅程</p>
      </Section>
    );
  }

  const nextTouch = formatTouchAt(journey.next_touch_at);

  return (
    <Section label="旅程">
      <p className="mt-1 text-[13px] font-semibold">{journey.journey_name || "—"}</p>
      {nextTouch ? <p className="mt-0.5 text-[11px] text-zinc-500">下次触达 · {nextTouch}</p> : null}
      <div className="mt-2">
        <Meta label="下一节点" value={journey.node_title} />
      </div>
    </Section>
  );
}

function MemorySection({
  externalUserId,
  initial,
}: {
  externalUserId: string;
  initial: string | null;
}) {
  const [summary, setSummary] = useState(initial);

  return (
    <CustomerMemoryIntake
      variant="sidebar"
      summary={summary}
      onSummaryChange={setSummary}
      propose={async (facts) => {
        const token = readSidebarToken();
        if (!token) throw new Error("登录已失效，请重新打开工具栏");
        return proposeSidebarMemory(token, externalUserId, facts);
      }}
      apply={async (next) => {
        const token = readSidebarToken();
        if (!token) throw new Error("登录已失效，请重新打开工具栏");
        const res = await updateSidebarMemory(token, externalUserId, next);
        return res.memory_summary;
      }}
    />
  );
}

function getZonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

function formatTouchAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const target = getZonedParts(date, SHANGHAI_TZ);
  const now = new Date();
  const today = getZonedParts(now, SHANGHAI_TZ);
  const tomorrow = getZonedParts(new Date(now.getTime() + 86_400_000), SHANGHAI_TZ);

  const time = `${target.hour.padStart(2, "0")}:${target.minute.padStart(2, "0")}`;
  const dayKey = `${target.year}-${target.month}-${target.day}`;

  if (dayKey === `${tomorrow.year}-${tomorrow.month}-${tomorrow.day}`) {
    return `明天 ${time}`;
  }
  if (dayKey === `${today.year}-${today.month}-${today.day}`) {
    return `今天 ${time}`;
  }

  const month = Number.parseInt(target.month, 10);
  const day = Number.parseInt(target.day, 10);
  return `${month}月${day}日 ${time}`;
}

const ALLOWED_ENTRIES = new Set(["single_chat_tools", "contact_profile", "single_kf_tools"]);

async function resolveExternalContact(
  token: string,
  app: { corpId: string | null; agentId: string | null },
): Promise<IdentifyResult> {
  if (!app.corpId || !app.agentId) {
    return { ok: false, reason: "sdk", message: "该账户尚未完成企微接入" };
  }

  ww.register({
    corpId: app.corpId,
    agentId: app.agentId,
    jsApiList: ["getContext", "getCurExternalContact"],
    async getConfigSignature(url) {
      const cfg = await fetchJsapiConfig(token, stripHash(url));
      return { timestamp: cfg.timestamp, nonceStr: cfg.nonce_str, signature: cfg.signature };
    },
    async getAgentConfigSignature(url) {
      const cfg = await fetchJsapiConfig(token, stripHash(url));
      return { timestamp: cfg.timestamp, nonceStr: cfg.nonce_str, signature: cfg.agent_signature };
    },
  });

  try {
    await ww.ensureConfigReady();
    const context = await ww.getContext();
    const entry = String(context.entry ?? "");
    if (entry && !ALLOWED_ENTRIES.has(entry)) {
      return {
        ok: false,
        reason: "wrong_entry",
        message: `当前入口为 ${entry}，请从外部客户的单聊工具栏打开`,
      };
    }

    const contact = await ww.getCurExternalContact();
    const id = String(contact.userId ?? "").trim();
    if (!id) {
      return { ok: false, reason: "wrong_entry", message: "未返回当前外部联系人" };
    }
    return { ok: true, externalUserId: id };
  } catch (error) {
    const message = sdkErrorMessage(error);
    if (/without context|no permission|nopermission/i.test(message)) {
      return { ok: false, reason: "wrong_entry", message };
    }
    return { ok: false, reason: "sdk", message };
  }
}

function sdkErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object" && error !== null) {
    const rec = error as Record<string, unknown>;
    const msg = rec.errMsg ?? rec.err_msg ?? rec.message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return "JS-SDK 调用失败";
}
